import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
  InputAdornment
} from "@mui/material";
import {
  CheckOutlined,
  EditOutlined,
  OpenInNewOutlined
} from "@mui/icons-material";
import {
  CommentDto,
  LegalLifecycle,
  TradingCounterParty
} from "../../../../../../api/Api";
import {
  useAssignees,
  useCounterpartyProgressOverride,
  useUpdateCounterparty
} from "../../../../dataHooks/counterpartyQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../sharedComponents/commentsSection";
import { LifecycleDialog } from "../../sharedComponents/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificCounterparty: TradingCounterParty;
};

type FormData = {
  agreementReview: string;
  agreementReviewAssignee: string;
  ndaReview: string;
  ndaReviewAssignee: string;
  comments: CommentDto[];
  clearContractCase: string;
  signOffNote: string;
};

export const Legal: FC<Props> = ({ specificCounterparty }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("counterparty");
  const roles = useRoles();
  const progressOverride = useCounterpartyProgressOverride(
    specificCounterparty,
    "legal"
  );

  const [formData, setFormData] = useState<FormData>({
    agreementReview:
      specificCounterparty?.legalLifecycle?.agreementReview || "",
    agreementReviewAssignee:
      specificCounterparty?.legalLifecycle?.agreementReviewAssignee || "",
    ndaReview: specificCounterparty?.legalLifecycle?.ndaReview || "",
    ndaReviewAssignee:
      specificCounterparty?.legalLifecycle?.ndaReviewAssignee || "",
    comments: specificCounterparty?.legalLifecycle?.comments || [],
    clearContractCase:
      specificCounterparty?.legalLifecycle?.clearContractCase || "",
    signOffNote: specificCounterparty?.legalLifecycle?.signOffNote || ""
  });

  const ndaRequired =
    specificCounterparty.marketAccessLifecycle?.ndaReview !== undefined &&
    specificCounterparty.marketAccessLifecycle.ndaReview !== "not_needed";

  const [isClearContractCaseEditable, setIsClearContractCaseEditable] =
    useState(false);

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const updateCounterparty = useUpdateCounterparty({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateCounterparty();

  const buildCounterparty = (
    legalLifecycle: LegalLifecycle
  ): TradingCounterParty => ({
    ...specificCounterparty,
    legalLifecycle: {
      ...specificCounterparty.legalLifecycle,
      ...legalLifecycle
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (updatedComments) =>
      buildCounterparty({
        ...formData,
        comments: updatedComments
      })
  });

  const legalAssignees = useAssignees("legal");
  const clearContractCaseUrl = buildValidUrl(formData.clearContractCase);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleClearContractCaseEditing = () => {
    setIsClearContractCaseEditable((prev) => !prev);
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificCounterparty.progress?.legal,
    canSetInProgress: ownsLifecycle(roles, "legal"),
    buildEntity: (legalLifecycle, progress) => ({
      ...buildCounterparty(legalLifecycle),
      progress: { ...specificCounterparty.progress, legal: progress }
    }),
    mutate: updateCounterparty.mutate
  });

  const open = location.pathname.includes("legal");

  const canComplete =
    formData.agreementReview && (ndaRequired ? formData.ndaReview : true);

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Legal"
      progressOverride={progressOverride}
      maxWidth="md"
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateCounterparty.isPending
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Agreement review</InputLabel>
            <Select
              label="Agreement review"
              value={formData.agreementReview}
              onChange={(e) => handleChange("agreementReview", e.target.value)}
            >
              <MenuItem value="initiated">
                <Typography variant="body1" component="span">
                  Initiated
                </Typography>
              </MenuItem>
              <MenuItem value="approved">
                <Typography variant="body1" component="span">
                  Approved
                </Typography>
              </MenuItem>
              <MenuItem value="rejected">
                <Typography variant="body1" component="span">
                  Rejected
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={legalAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              legalAssignees.data?.find(
                (a) => a.email === formData.agreementReviewAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("agreementReviewAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agreement review assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {ndaRequired && (
          <Box display="flex" gap={2}>
            <FormControl size="medium" required sx={{ flex: 1 }}>
              <InputLabel>NDA Review</InputLabel>
              <Select
                label="NDA Review"
                value={formData.ndaReview}
                onChange={(e) => handleChange("ndaReview", e.target.value)}
              >
                <MenuItem value="initiated">
                  <Typography variant="body1" component="span">
                    Initiated
                  </Typography>
                </MenuItem>
                <MenuItem value="approved">
                  <Typography variant="body1" component="span">
                    Approved
                  </Typography>
                </MenuItem>
                <MenuItem value="rejected">
                  <Typography variant="body1" component="span">
                    Rejected
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ flex: 1 }}
              options={legalAssignees.data || []}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={
                legalAssignees.data?.find(
                  (a) => a.email === formData.ndaReviewAssignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange("ndaReviewAssignee", newValue?.email || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="NDA review assignee"
                  size="medium"
                />
              )}
            />
          </Box>
        )}

        <TextField
          label="Clear Contract case link"
          value={formData.clearContractCase}
          onChange={(e) => handleChange("clearContractCase", e.target.value)}
          fullWidth
          disabled={!isClearContractCaseEditable}
          slotProps={{
            input: {
              startAdornment: clearContractCaseUrl ? (
                <InputAdornment position="start">
                  <Tooltip title="Open clear contract case link">
                    <IconButton
                      onClick={() =>
                        window.open(
                          clearContractCaseUrl,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                      edge="start"
                      color="primary"
                    >
                      <OpenInNewOutlined />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      isClearContractCaseEditable
                        ? "Lock clear contract case"
                        : "Unlock clear contract case for editing"
                    }
                  >
                    <IconButton
                      onClick={toggleClearContractCaseEditing}
                      edge="end"
                      color={
                        isClearContractCaseEditable ? "secondary" : "primary"
                      }
                    >
                      {isClearContractCaseEditable ? (
                        <CheckOutlined />
                      ) : (
                        <EditOutlined />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }
          }}
          autoFocus={isClearContractCaseEditable}
          helperText={
            isClearContractCaseEditable
              ? "Editing enabled until you lock it again or save the dialog."
              : clearContractCaseUrl
                ? "Disabled for editing. Use the open icon to follow the link."
                : "Locked to reduce accidental changes."
          }
        />

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};

const buildValidUrl = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  try {
    const url = new URL(trimmedValue);

    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
};
